import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useActiveHomeUnitId } from '../hooks/useActiveHomeUnitId';
import { Settings, Save, X, Plus, LayoutGrid, GripVertical, Trash2 } from 'lucide-react';
import { Button } from './ui/Button';
import toast from '../lib/toast';

// Import all widget components
import { StatsWidget } from './widgets/StatsWidget';
import { FinanceWidget } from './widgets/FinanceWidget';
import { AmenitiesWidget } from './widgets/AmenitiesWidget';
import { VisitorsWidget } from './widgets/VisitorsWidget';
import { ComplaintsWidget } from './widgets/ComplaintsWidget';
import { AnnouncementsWidget } from './widgets/AnnouncementsWidget';
import { VotingWidget } from './widgets/VotingWidget';
import { ActivityWidget } from './widgets/ActivityWidget';
import { PendingApprovalsWidget } from './widgets/PendingApprovalsWidget';

// Widget registry
const WIDGET_REGISTRY: Record<string, {
  title: string;
  component: React.FC;
  moduleRequired?: string;
  defaultVisible: boolean;
}> = {
  stats: {
    title: 'Estadísticas Principales',
    component: StatsWidget,
    defaultVisible: true,
  },
  finance: {
    title: 'Finanzas',
    component: FinanceWidget,
    moduleRequired: 'finanzas',
    defaultVisible: true,
  },
  amenities: {
    title: 'Amenidades',
    component: AmenitiesWidget,
    moduleRequired: 'amenidades',
    defaultVisible: true,
  },
  visitors: {
    title: 'Visitantes',
    component: VisitorsWidget,
    moduleRequired: 'visitantes',
    defaultVisible: true,
  },
  complaints: {
    title: 'Quejas y Sugerencias',
    component: ComplaintsWidget,
    moduleRequired: 'quejas',
    defaultVisible: false,
  },
  announcements: {
    title: 'Anuncios',
    component: AnnouncementsWidget,
    moduleRequired: 'anuncios',
    defaultVisible: true,
  },
  voting: {
    title: 'Votaciones',
    component: VotingWidget,
    moduleRequired: 'votaciones',
    defaultVisible: false,
  },
  activity: {
    title: 'Actividad Reciente',
    component: ActivityWidget,
    defaultVisible: true,
  },
  pending: {
    title: 'Pendientes de Aprobación',
    component: PendingApprovalsWidget,
    defaultVisible: true,
  },
};

// Default layout order
const DEFAULT_LAYOUT = ['stats', 'finance', 'amenities', 'visitors', 'activity', 'pending', 'announcements'];

interface WidgetItem {
  id: string;
  visible: boolean;
}

export function SimpleCustomizableDashboard() {
  const { user } = useAuth();
  const { hasModuleAccess } = useModuleAccess();
  // FIX (2026-09-03, Jaime): layout preset was keyed ONLY per user
  // (`dashboardLayout_${user.id}`), so it leaked across units and any
  // mismatch silently discarded the saved layout. Now the preset is scoped
  // per user AND per active unit; the legacy per-user key is migrated once.
  const activeUnitId = useActiveHomeUnitId();
  // Units without an id (no active selection) share the 'default' bucket so
  // the pre-unit-scoping behavior is preserved there.
  const unitScope = activeUnitId || 'default';
  const layoutKey = user?.id ? `dashboardLayout_${user.id}_${unitScope}` : null;
  const visibleKey = user?.id ? `dashboardVisible_${user.id}_${unitScope}` : null;
  // Legacy per-user keys (pre 2026-09-03): read once for migration.
  const legacyLayoutKey = user?.id ? `dashboardLayout_${user.id}` : null;
  const legacyVisibleKey = user?.id ? `dashboardVisible_${user.id}` : null;
  const [isEditMode, setIsEditMode] = useState(false);
  const [layout, setLayout] = useState<string[]>([]);
  const [visibleWidgets, setVisibleWidgets] = useState<Set<string>>(new Set());
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Load saved layout on mount (and whenever the user or active unit changes).
  useEffect(() => {
    if (!layoutKey || !visibleKey) return;
    const loadLayout = () => {
      // Per-unit keys first; fall back to (and migrate from) the legacy
      // per-user keys so existing presets survive the key change.
      let savedLayoutRaw = localStorage.getItem(layoutKey);
      let savedVisibleRaw = localStorage.getItem(visibleKey);
      if (savedLayoutRaw == null && savedVisibleRaw == null && legacyLayoutKey && legacyVisibleKey) {
        savedLayoutRaw = localStorage.getItem(legacyLayoutKey);
        savedVisibleRaw = localStorage.getItem(legacyVisibleKey);
        if (savedLayoutRaw != null && savedVisibleRaw != null) {
          // Migrate: write to the new per-unit keys. Legacy keys are left in
          // place (non-destructive) so an older build can still read them.
          try {
            localStorage.setItem(layoutKey, savedLayoutRaw);
            localStorage.setItem(visibleKey, savedVisibleRaw);
          } catch {
            /* storage full/blocked: keep in-memory preset for this session */
          }
        }
      }

      if (savedLayoutRaw != null && savedVisibleRaw != null) {
        let savedLayout: string[] = [];
        let savedVisible: string[] = [];
        try {
          const parsedLayout = JSON.parse(savedLayoutRaw);
          const parsedVisible = JSON.parse(savedVisibleRaw);
          if (Array.isArray(parsedLayout)) savedLayout = parsedLayout.filter((id): id is string => typeof id === 'string');
          if (Array.isArray(parsedVisible)) savedVisible = parsedVisible.filter((id): id is string => typeof id === 'string');
        } catch (e) {
          console.error('Error loading layout:', e);
          // FIX: a partial/corrupt parse must NOT wipe the saved preset.
          // Fall through with whatever parsed; sanitizeAndMerge below keeps
          // every recoverable widget id and only appends missing defaults.
        }
        const { layout: mergedLayout, visible: mergedVisible } = sanitizeAndMerge(savedLayout, savedVisible, hasModuleAccess);
        setLayout(mergedLayout);
        setVisibleWidgets(mergedVisible);
      } else {
        // Nothing saved for this user+unit (and no legacy data): first visit.
        resetToDefault();
      }
    };
    
    loadLayout();
  }, [user?.id, unitScope, layoutKey, visibleKey, legacyLayoutKey, legacyVisibleKey, hasModuleAccess]);

  // FIX (2026-09-03, Jaime): explicit, user-triggered factory reset only.
  // Never called on load-path mismatches anymore.
  const resetToDefault = () => {
    // Filter by module access and default visibility
    const accessible = DEFAULT_LAYOUT.filter(id => {
      const config = WIDGET_REGISTRY[id];
      if (!config) return false;
      if (config.moduleRequired) {
        return hasModuleAccess(config.moduleRequired) && config.defaultVisible;
      }
      return config.defaultVisible;
    });
    
    setLayout(accessible);
    setVisibleWidgets(new Set(accessible));
  };

  // Merge a saved layout with the current defaults:
  //  - drop unknown ids (widget removed in a newer deploy) but KEEP every id
  //    that still exists in the registry, even if its module gate changed —
  //    unknown ids must never cause the whole preset to be discarded;
  //  - append any default widgets that are missing so new deploys' widgets
  //    show up instead of a blank dashboard.
  const sanitizeAndMerge = (
    savedLayout: string[],
    savedVisible: string[],
    hasAccess: (moduleId: string) => boolean,
  ): { layout: string[]; visible: Set<string> } => {
    const known = savedLayout.filter(id => !!WIDGET_REGISTRY[id]);
    const missingDefaults = DEFAULT_LAYOUT.filter(id => !known.includes(id));
    const merged = [...known, ...missingDefaults];
    const visible = new Set(savedVisible.filter(id => merged.includes(id)));
    merged.forEach(id => {
      const config = WIDGET_REGISTRY[id];
      if (!config) return;
      if (config.moduleRequired && !hasAccess(config.moduleRequired)) {
        visible.delete(id);
        return;
      }
      // Widgets that were never part of a saved visibility list adopt the
      // registry default instead of defaulting to hidden.
      if (!savedVisible.includes(id) && config.defaultVisible) visible.add(id);
    });
    return { layout: merged, visible };
  };

  // Filter to only show accessible and visible widgets
  const displayWidgets = layout.filter(id => {
    const config = WIDGET_REGISTRY[id];
    if (!config || !visibleWidgets.has(id)) return false;
    if (config.moduleRequired) {
      return hasModuleAccess(config.moduleRequired);
    }
    return true;
  });

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, widgetId: string) => {
    setDraggedItem(widgetId);
    e.dataTransfer.effectAllowed = 'move';
    // Required for Firefox
    e.dataTransfer.setData('text/plain', widgetId);
  };

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (!draggedItem || draggedItem === targetId) return;
    
    // Reorder layout
    const newLayout = [...layout];
    const draggedIndex = newLayout.indexOf(draggedItem);
    const targetIndex = newLayout.indexOf(targetId);
    
    if (draggedIndex > -1 && targetIndex > -1) {
      newLayout.splice(draggedIndex, 1);
      newLayout.splice(targetIndex, 0, draggedItem);
      setLayout(newLayout);
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleToggleVisibility = (widgetId: string) => {
    setVisibleWidgets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(widgetId)) {
        newSet.delete(widgetId);
      } else {
        newSet.add(widgetId);
        // Add to layout if not present
        if (!layout.includes(widgetId)) {
          setLayout(l => [...l, widgetId]);
        }
      }
      return newSet;
    });
  };

  const handleSave = () => {
    if (!layoutKey || !visibleKey) return;
    localStorage.setItem(layoutKey, JSON.stringify(layout));
    localStorage.setItem(visibleKey, JSON.stringify([...visibleWidgets]));
    toast.success('Dashboard guardado correctamente');
    setIsEditMode(false);
  };

  const handleReset = () => {
    if (confirm('¿Restaurar layout por defecto?')) {
      resetToDefault();
      toast.success('Layout restaurado');
    }
  };

  const renderWidget = (widgetId: string) => {
    const config = WIDGET_REGISTRY[widgetId];
    if (!config) return null;
    
    const WidgetComponent = config.component;
    
    return (
      <div
        key={widgetId}
        draggable={isEditMode}
        onDragStart={(e) => handleDragStart(e, widgetId)}
        onDragOver={(e) => handleDragOver(e, widgetId)}
        onDragEnd={handleDragEnd}
        className={`
          bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden
          transition-all duration-200
          ${isEditMode ? 'cursor-move hover:shadow-md' : ''}
          ${draggedItem === widgetId ? 'opacity-50 ring-2 ring-[#36CCD8]' : ''}
        `}
      >
        {/* Widget Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            {isEditMode && (
              <GripVertical className="w-4 h-4 text-gray-400" />
            )}
            <h3 className="font-semibold text-gray-800">{config.title}</h3>
          </div>
          {isEditMode && (
            <button
              onClick={() => handleToggleVisibility(widgetId)}
              className="p-1.5 hover:bg-red-100 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
              title="Ocultar widget"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Widget Content */}
        <div className="p-4">
          <WidgetComponent />
        </div>
      </div>
    );
  };

  // Available widgets to add
  const availableToAdd = Object.entries(WIDGET_REGISTRY)
    .filter(([id, config]) => {
      if (visibleWidgets.has(id)) return false;
      if (config.moduleRequired) {
        return hasModuleAccess(config.moduleRequired);
      }
      return true;
    });

  return (
    <div className="min-h-full bg-[var(--v-base-bg)] p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600">Personaliza tu vista</p>
          </div>
          <div className="flex items-center gap-3">
            {isEditMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowAddModal(true)}
                  disabled={availableToAdd.length === 0}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Agregar ({availableToAdd.length})
                </Button>
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="flex items-center gap-2"
                >
                  <LayoutGrid className="w-4 h-4" />
                  Restaurar
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-[#36CCD8] hover:bg-[#2DB8C4] text-white flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Guardar
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setIsEditMode(false)}
                  className="flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={() => setIsEditMode(true)}
                className="flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Personalizar
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Edit Mode Banner */}
      {isEditMode && (
        <div className="max-w-7xl mx-auto mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
            <GripVertical className="w-5 h-5 text-blue-600" />
            <p className="text-blue-800">
              <strong>Modo Edición:</strong> Arrastra los widgets para reorganizar. 
              Usa el ícono de basura para ocultar.
            </p>
          </div>
        </div>
      )}

      {/* Add Widget Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Agregar Widget</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-auto">
              {availableToAdd.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No hay widgets disponibles para agregar
                </p>
              ) : (
                <div className="space-y-3">
                  {availableToAdd.map(([id, config]) => (
                    <button
                      key={id}
                      onClick={() => {
                        handleToggleVisibility(id);
                        setShowAddModal(false);
                      }}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-[#36CCD8] hover:bg-[#36CCD8]/5 transition-all text-left"
                    >
                      <h3 className="font-semibold text-gray-900">{config.title}</h3>
                      {config.moduleRequired && (
                        <p className="text-sm text-gray-500 mt-1">
                          Módulo: {config.moduleRequired}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Widget Grid */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {displayWidgets.map(renderWidget)}
        </div>

        {/* Empty State */}
        {displayWidgets.length === 0 && (
          <div className="text-center py-20">
            <LayoutGrid className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No hay widgets visibles
            </h3>
            <p className="text-gray-500 mb-6">
              Haz clic en "Personalizar" para agregar widgets
            </p>
            <Button
              onClick={() => setIsEditMode(true)}
              className="bg-[#36CCD8] hover:bg-[#2DB8C4] text-white"
            >
              Personalizar Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SimpleCustomizableDashboard;
